<template>
  <div class="admin-work-pool-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminWorkPool.list.title')" :subtitle="$t('adminWorkPool.page.subtitle')" />

    <div class="admin-work-pool-layout">
      <!-- 左侧小区选择 -->
      <div class="ds-panel ds-panel--side">
        <select-admin-community @changeCommunity="handleCommunityChange" />
      </div>

      <div class="admin-work-pool-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.workNameLike" clearable class="w-full"
                :placeholder="$t('adminWorkPool.search.workName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.createUserNameLike" clearable class="w-full"
                :placeholder="$t('adminWorkPool.search.createUser')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.staffNameLike" clearable class="w-full"
                :placeholder="$t('adminWorkPool.search.staffName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.queryStartTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('adminWorkPool.search.startTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.queryEndTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('adminWorkPool.search.endTime')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminWorkPool.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminWorkPool.page.empty')" @retry="getList">
            <el-table-column prop="workId" :label="$t('adminWorkPool.table.id')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.workId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('adminWorkPool.table.communityName')" align="center" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.communityName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="workName" :label="$t('adminWorkPool.table.workName')" align="left" min-width="150"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.workName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="typeName" :label="$t('adminWorkPool.table.typeName')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.typeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminWorkPool.table.workCycle')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.workCycle === '1001' ? $t('adminWorkPool.workCycle.once') :
                  $t('adminWorkPool.workCycle.periodic') }}
              </template>
            </el-table-column>
            <el-table-column prop="createUserName" :label="$t('adminWorkPool.table.createUser')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.createUserName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="staffName" :label="$t('adminWorkPool.table.staffName')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.staffName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminWorkPool.table.timeRange')" align="center" width="180">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
                <div class="ds-text-muted ds-num" v-if="scope.row.endTime">~{{ scope.row.endTime }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminWorkPool.table.status')" align="center" width="130">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
                  :type="getStateType(scope.row.state, scope.row.taskTimeout)" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('adminWorkPool.table.createTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="finishTime" :label="$t('adminWorkPool.table.finishTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.finishTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="primary" @click="handleDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listAdminWorkTask } from '@/api/work/adminWorkPoolApi'
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AdminWorkPoolList',
  components: {
    SelectAdminCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        workNameLike: '',
        createUserNameLike: '',
        staffNameLike: '',
        queryStartTime: '',
        queryEndTime: '',
        communityId: '',
        state: '',
        page: 1,
        row: 10
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = { ...this.searchForm }
        if (params.state === 'timeout') {
          params.state = 'C'
          params.taskTimeout = 'Y'
        }
        const { data, total } = await listAdminWorkTask(params)
        this.tableData = data
        this.pagination.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminWorkPool.fetchError')
        this.$message.error(this.$t('adminWorkPool.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.searchForm.page = 1
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        workNameLike: '',
        createUserNameLike: '',
        staffNameLike: '',
        queryStartTime: '',
        queryEndTime: '',
        communityId: this.searchForm.communityId,
        state: '',
        page: 1,
        row: 10
      }
      this.getList()
    },
    handleCommunityChange(community) {
      this.searchForm.communityId = community.communityId
      this.handleSearch()
    },
    handleDetail(row) {
      this.$router.push(`/views/work/adminWorkDetail?workId=${row.workId}`)
    },
    handleSizeChange(val) {
      this.searchForm.row = val
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handlePageChange(val) {
      this.searchForm.page = val
      this.pagination.current = val
      this.getList()
    },
    getStateType(state, taskTimeout) {
      if (state === 'C' && taskTimeout === 'Y') return 'danger'
      if (state === 'C') return 'success'
      if (state === 'D') return 'muted'
      return 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-work-pool-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧小区 + 右侧内容 */
.admin-work-pool-layout {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-spacing-lg);
}

.admin-work-pool-main {
  flex: 1;
  min-width: 0;
}

/* 左侧面板 */
.ds-panel--side {
  width: 250px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

@media (max-width: 1200px) {
  .admin-work-pool-layout {
    flex-direction: column;
  }

  .ds-panel--side {
    width: 100%;
  }
}
</style>
